import { useState, useCallback } from 'react';
import { useMutation } from 'convex/react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: hooks in src/hooks/ need convex api
import type { Id } from '../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: convex _generated
import type { IEmployeeDocument } from '@/@types/company';

export function useNewEmployeeForm() {
  const navigate = useNavigate();
  const createEmployee = useMutation(api.companies.employees.create);

  // Basic info
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [position, setPosition] = useState('');

  // Personal info
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [identityDocumentNumber, setIdentityDocumentNumber] = useState('');
  const [nationality, setNationality] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');

  // Documents
  const [identityDocFront, setIdentityDocFront] = useState<IEmployeeDocument | undefined>(
    undefined
  );
  const [identityDocBack, setIdentityDocBack] = useState<IEmployeeDocument | undefined>(undefined);
  const [drivingLicenseFront, setDrivingLicenseFront] = useState<IEmployeeDocument | undefined>(
    undefined
  );
  const [drivingLicenseBack, setDrivingLicenseBack] = useState<IEmployeeDocument | undefined>(
    undefined
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDocumentChange = useCallback(
    (
      field: 'identityDocFront' | 'identityDocBack' | 'drivingLicenseFront' | 'drivingLicenseBack',
      doc: IEmployeeDocument | null
    ) => {
      const value = doc ?? undefined;
      if (field === 'identityDocFront') setIdentityDocFront(value);
      else if (field === 'identityDocBack') setIdentityDocBack(value);
      else if (field === 'drivingLicenseFront') setDrivingLicenseFront(value);
      else if (field === 'drivingLicenseBack') setDrivingLicenseBack(value);
    },
    []
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent, companyId: Id<'companies'>) => {
      e.preventDefault();
      if (!name.trim()) {
        toast.error('Name is required');
        return;
      }
      setIsSubmitting(true);
      try {
        await createEmployee({
          companyId,
          name: name.trim(),
          email: email.trim() || undefined,
          phoneNumber: phoneNumber.trim() || undefined,
          position: position.trim() || undefined,
          postalCode: postalCode.trim() || undefined,
          city: city.trim() || undefined,
          identityDocumentNumber: identityDocumentNumber.trim() || undefined,
          nationality: nationality.trim() || undefined,
          dateOfBirth: dateOfBirth || undefined,
          identityDocFront,
          identityDocBack,
          drivingLicenseFront,
          drivingLicenseBack,
        });
        toast.success('Employee added');
        navigate('/company/employees');
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to add employee');
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      createEmployee,
      navigate,
      name,
      email,
      phoneNumber,
      position,
      postalCode,
      city,
      identityDocumentNumber,
      nationality,
      dateOfBirth,
      identityDocFront,
      identityDocBack,
      drivingLicenseFront,
      drivingLicenseBack,
    ]
  );

  return {
    // Field values
    name,
    email,
    phoneNumber,
    position,
    postalCode,
    city,
    identityDocumentNumber,
    nationality,
    dateOfBirth,
    identityDocFront,
    identityDocBack,
    drivingLicenseFront,
    drivingLicenseBack,
    isSubmitting,
    // Setters
    setName,
    setEmail,
    setPhoneNumber,
    setPosition,
    setPostalCode,
    setCity,
    setIdentityDocumentNumber,
    setNationality,
    setDateOfBirth,
    // Handlers
    handleDocumentChange,
    handleSubmit,
  };
}
